import * as Sentry from '@sentry/browser';
import { useCookies } from 'react-cookie';

import { AdminSellerCompanyRoomInfo } from '@type-def_iqfu/chat/AdminSellerCompanyRoomInfo';
import { CompanyFinancialAgencyRoomInfo } from '@type-def_iqfu/chat/CompanyFinancialAgencyRoomInfo';
import { CompanySellerRoomInfo } from '@type-def_iqfu/chat/CompanySellerRoomInfo';

import { UserType } from '@constants_common';
import { ChatPropertyType } from '@constants_iqfu';

const queryString = require('query-string');

const backendBasePath = process.env.NEXT_PUBLIC_DEV_BACKEND_URL;

const hasValidLimitOffsetParams = (limit?: unknown, offset?: unknown) => {
  const hasValidLimit = typeof limit === 'number' && limit >= 0;
  const hasValidOffset = typeof offset === 'number' && offset >= 0;
  if ((limit && !hasValidLimit) || (offset && !hasValidOffset)) {
    return false;
  } else {
    return true;
  }
};

//一本化エンドポイントの型定義
export type ChatItem = {
  id: number;
  content: string;
  file_name?: string; // Optional property, may be undefined
  content_type: string;
  is_deleted: boolean;
  is_deletable: boolean;
  sent_at: string; // Format: '2022-03-25T17:56:58+09:00'
  icon_url: string;
  icon_alternative: string;
  read_user_count?: number | null; // Optional property, may be undefined or null
  display_position: 'right' | 'left';
  display_sender_name?: string; // Optional property, may be undefined
  link_url?: string;
};

export const useChatRooms = () => {
  const [cookies] = useCookies(['expiry', 'access-token', 'client', 'uid', 'user-type']);

  const getCompanySellerChatRoomList = async (queryParams: {
    seller_name?: string;
    limit?: number;
    offset?: number;
    starred?: boolean;
  }) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return;

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/company/chat_rooms?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getCompanySellerContactChatRoomList = async (queryParams: {
    seller_name?: string;
    limit?: number;
    offset?: number;
  }) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return;

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/company/chat_seller_company_rooms?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getFinancialAgencySellerChatRoomList = async (queryParams: { limit?: number; offset?: number }) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return;

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/financial_agency/chat_seller_financial_agency_division_rooms?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getCompanyAdminRoomList = async (queryParams: { limit?: number; offset?: number }) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return;

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/company/chat_company_inquiries?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getCompanyFinancialAgencyRoomList = async (queryParams: { limit?: number; offset?: number }) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return;

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/company/chat_consulting_rooms?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getFinancialAgencyAdminRoomList = async (queryParams: { limit?: number; offset?: number }) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return;

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/financial_agency/chat_financial_agency_division_inquiries?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getFinancialAgencyCompanyRoomList = async (queryParams: { limit?: number; offset?: number }) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return;

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/financial_agency/chat_consulting_rooms?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getAdminSellerRoomList = async (queryParams: {
    seller_name?: string;
    limit?: number;
    offset?: number;
    action_required: boolean;
    support_result_confirming: boolean;
    priority?: 'high';
    response_pending?: boolean;
  }) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return;

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/admin/chat_user_inquiries?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getAdminCompanyRoomList = async (queryParams: {
    company_name?: string;
    limit?: number;
    offset?: number;
    action_required: boolean;
    message_scheduled: boolean;
    unread: boolean;
    reply_required: boolean;
  }) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return;

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/admin/chat_company_inquiries?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getAdminSellerFinancialAgencyRoomList = async (queryParams: { limit?: number; offset?: number }) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return;

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/admin/chat_seller_financial_agency_division_rooms?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getAdminSellerCompanyRoomList = async (queryParams: {
    seller_name?: string;
    company_id?: string;
    property_type?: keyof typeof ChatPropertyType;
    status?: string;
    limit?: number;
    offset?: number;
    priority?: 'high';
  }) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return;

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/admin/chat_rooms?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getAdminSellerCompanyContactRoomList = async (queryParams: { limit?: number; offset?: number }) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return;

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/admin/chat_seller_company_rooms?${query}`;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getAdminFinancialAgencyRoomList = async (queryParams: { limit?: number; offset?: number }) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return;

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/admin/chat_financial_agency_division_inquiries?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getAdminFinancialAgencyCompanyRoomList = async (queryParams: { limit?: number; offset?: number }) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return { status: 404 };

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/admin/chat_consulting_rooms?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getCompanySellerTalk = async (roomId: number, queryParams: { limit?: number; offset?: number }) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return { status: 404 };

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/company/chat_rooms/${roomId}/chat_items?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        return { status: 404 };
      });
    return result;
  };

  const getCompanySellerContactTalk = async (roomId: number, queryParams: { limit?: number; offset?: number }) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return { status: 404 };

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/company/chat_seller_company_rooms/${roomId}/chat_items?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        return { status: 404 };
      });
    return result;
  };

  const getCompanyAdminTalk = async (roomId: number, queryParams: { limit?: number; offset?: number }) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return { status: 404 };

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/company/chat_company_inquiries/${roomId}/inquiry_chat_items?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        return { status: 404 };
      });
    return result;
  };

  const getCompanyFinancialAgencyTalk = async (roomId: number, queryParams: { limit?: number; offset?: number }) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return { status: 404 };

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/company/chat_consulting_rooms/${roomId}/chat_items?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        return { status: 404 };
      });
    return result;
  };

  const getAdminSellerTalk = async (roomId: number, queryParams: { limit?: number; offset?: number }) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return { status: 404 };

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/admin/chat_user_inquiries/${roomId}/chat_user_inquiry_items?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        return { status: 404 };
      });
    return result;
  };

  const getAdminCompanyTalk = async (roomId: number, queryParams: { limit?: number; offset?: number }) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return { status: 404 };

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/admin/chat_company_inquiries/${roomId}/chat_company_inquiry_items?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        return { status: 404 };
      });
    return result;
  };

  const getFinancialAgencyAdminTalk = async (roomId: number, queryParams: { limit?: number; offset?: number }) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return { status: 404 };

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/financial_agency/chat_financial_agency_division_inquiries/${roomId}/chat_items?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        return { status: 404 };
      });
    return result;
  };

  const getFinancialAgencyCompanyTalk = async (roomId: number, queryParams: { limit?: number; offset?: number }) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return;

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/financial_agency/chat_consulting_rooms/${roomId}/chat_items?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        return { status: 404 };
      });
    return result;
  };

  const getFinancialAgencySellerTalk = async (roomId: number, queryParams: { limit?: number; offset?: number }) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return;

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/financial_agency/chat_seller_financial_agency_division_rooms/${roomId}/chat_items?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        return { status: 404 };
      });
    return result;
  };

  const getAdminSellerCompanyTalk = async (roomId: number, queryParams: { limit?: number; offset?: number }) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return;

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/admin/chat_rooms/${roomId}/chat_items?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        return { status: 404 };
      });
    return result;
  };

  const getAdminSellerCompanyContactTalk = async (roomId: number, queryParams: { limit?: number; offset?: number }) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return;

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/admin/chat_seller_company_rooms/${roomId}/chat_items?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        return { status: 404 };
      });
    return result;
  };

  const getAdminFinancialAgencyTalk = async (roomId: number, queryParams: { limit?: number; offset?: number }) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return;

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/admin/chat_financial_agency_division_inquiries/${roomId}/chat_items?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        return { status: 404 };
      });
    return result;
  };

  const getAdminFinancialAgencyCompanyTalk = async (
    roomId: number,
    queryParams: { limit?: number; offset?: number },
  ) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return;

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/admin/chat_consulting_rooms/${roomId}/chat_items?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        return { status: 404 };
      });
    return result;
  };

  const getAdminSellerFinancialAgencyTalk = async (
    roomId: number,
    queryParams: { limit?: number; offset?: number },
  ) => {
    if (!hasValidLimitOffsetParams(queryParams.limit, queryParams.offset)) return;

    const query = queryString.stringify(queryParams);
    const url = `${backendBasePath}/admin/chat_seller_financial_agency_division_rooms/${roomId}/chat_items?${query}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
        return { status: 404 };
      });
    return result;
  };

  const createCompanySellerTalk = async ({ roomId, submitData }: { roomId: number; submitData: FormData }) => {
    const url = `${backendBasePath}/company/chat_rooms/${roomId}/chat_items`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
      body: submitData,
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const createCompanySellerContactTalk = async ({ roomId, submitData }: { roomId: number; submitData: FormData }) => {
    const url = `${backendBasePath}/company/chat_seller_company_rooms/${roomId}/chat_items`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
      body: submitData,
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const createCompanyAdminTalk = async ({ roomId, submitData }: { roomId: number; submitData: FormData }) => {
    const url = `${backendBasePath}/company/chat_company_inquiries/${roomId}/inquiry_chat_items`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
      body: submitData,
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const createCompanyFinancialAgencyTalk = async ({ roomId, submitData }: { roomId: number; submitData: FormData }) => {
    const url = `${backendBasePath}/company/chat_consulting_rooms/${roomId}/chat_items`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
      body: submitData,
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const createAdminSellerTalk = async ({ roomId, submitData }: { roomId: number; submitData: FormData }) => {
    const url = `${backendBasePath}/admin/chat_user_inquiries/${roomId}/chat_user_inquiry_items`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
      body: submitData,
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const createAdminCompanyTalk = async ({ roomId, submitData }: { roomId: number; submitData: FormData }) => {
    const url = `${backendBasePath}/admin/chat_company_inquiries/${roomId}/chat_company_inquiry_items`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
      body: submitData,
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const createAdminFinancialAgencyTalk = async ({ roomId, submitData }: { roomId: number; submitData: FormData }) => {
    const url = `${backendBasePath}/admin/chat_financial_agency_division_inquiries/${roomId}/chat_items`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
      body: submitData,
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const createFinancialAgencyCompanyTalk = async ({ roomId, submitData }: { roomId: number; submitData: FormData }) => {
    const url = `${backendBasePath}/financial_agency/chat_consulting_rooms/${roomId}/chat_items`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
      body: submitData,
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const createFinancialAgencyAdminTalk = async ({ roomId, submitData }: { roomId: number; submitData: FormData }) => {
    const url = `${backendBasePath}/financial_agency/chat_financial_agency_division_inquiries/${roomId}/chat_items`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
      body: submitData,
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const createFinancialAgencySellerTalk = async ({ roomId, submitData }: { roomId: number; submitData: FormData }) => {
    const url = `${backendBasePath}/financial_agency/chat_seller_financial_agency_division_rooms/${roomId}/chat_items`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
      body: submitData,
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const deleteCompanySellerTalk = async (chatItemId: number) => {
    const url = `${backendBasePath}/company/chat_items/${chatItemId}`;

    const result = await fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const deleteCompanySellerContactTalk = async (chatItemId: number) => {
    const url = `${backendBasePath}/company/chat_seller_company_items/${chatItemId}`;

    const result = await fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const deleteCompanyAdminTalk = async (chatItemId: number) => {
    const url = `${backendBasePath}/company/inquiry_chat_items/${chatItemId}`;

    const result = await fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const deleteCompanyFinancialAgencyTalk = async (chatItemId: number) => {
    const url = `${backendBasePath}/company/chat_consulting_items/${chatItemId}`;

    const result = await fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const deleteAdminSellerTalk = async (chatItemId: number) => {
    const url = `${backendBasePath}/admin/chat_user_inquiry_items/${chatItemId}`;

    const result = await fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const deleteAdminCompanyTalk = async (chatItemId: number) => {
    const url = `${backendBasePath}/admin/chat_company_inquiry_items/${chatItemId}`;

    const result = await fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const deleteAdminFinancialAgencyTalk = async (chatItemId: number) => {
    const url = `${backendBasePath}/admin/chat_financial_agency_division_inquiry_items/${chatItemId}`;

    const result = await fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const deleteFinancialAgencyAdminTalk = async (chatItemId: number) => {
    const url = `${backendBasePath}/financial_agency/inquiry_chat_items/${chatItemId}`;

    const result = await fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const deleteFinancialAgencyCompanyTalk = async (chatItemId: number) => {
    const url = `${backendBasePath}/financial_agency/chat_items/${chatItemId}`;

    const result = await fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const deleteFinancialAgencySellerTalk = async (chatItemId: number) => {
    const url = `${backendBasePath}/financial_agency/chat_seller_financial_agency_division_items/${chatItemId}`;

    const result = await fetch(url, {
      method: 'DELETE',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getCompanySellerRoomInfo = async (roomId: number): Promise<CompanySellerRoomInfo | undefined> => {
    if (!roomId) return;

    const url = `${backendBasePath}/company/chat_rooms/${roomId}`;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  type CompanySellerContactRoomInfo = {
    id: number;
    seller_user_id: number;
    seller_user_name: string;
    company_id: number;
    company_name: string;
    seller_user_tel: string;
    company_tel: string;
    company_image: string;
  };

  const getCompanySellerContactRoomInfo = async (roomId: number): Promise<CompanySellerContactRoomInfo | undefined> => {
    if (!roomId) return;

    const url = `${backendBasePath}/company/chat_seller_company_rooms/${roomId}`;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getCompanyFinancialAgencyRoomInfo = async (
    roomId: number,
  ): Promise<CompanyFinancialAgencyRoomInfo | undefined> => {
    if (!roomId) return;

    const url = `${backendBasePath}/company/chat_consulting_rooms/${roomId}`;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getAdminSellerCompanyRoomInfo = async (roomId: number): Promise<AdminSellerCompanyRoomInfo | undefined> => {
    if (!roomId) return;

    const url = `${backendBasePath}/admin/chat_rooms/${roomId}`;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  type AdminSellerCompanyContactRoomInfo = {
    id: number;
    seller_user_id: number;
    seller_user_name: string;
    seller_tel: string;
    company_id: number;
    company_name: string;
    company_tel: string;
    company_image: string;
  };

  const getAdminSellerCompanyContactRoomInfo = async (
    roomId: number,
  ): Promise<AdminSellerCompanyContactRoomInfo | undefined> => {
    if (!roomId) return;

    const url = `${backendBasePath}/admin/chat_seller_company_rooms/${roomId}`;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  type AdminSellerRoomInfo = {
    id: number;
    seller_user_id: number;
    seller_user_name: string;
    seller_tel: string;
    action_required: boolean;
    support_informed: boolean;
    support_completed: boolean;
    support_result_confirming: boolean;
  };

  const getAdminSellerRoomInfo = async (roomId: number): Promise<AdminSellerRoomInfo | undefined> => {
    if (!roomId) return;

    const url = `${backendBasePath}/admin/chat_user_inquiries/${roomId}`;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  type AdminCompanyRoomInfo = {
    id: number;
    company_id: number;
    company_name: string;
    company_image: string;
    company_tel: string;
    action_required: boolean;
    reply_required: boolean;
  };

  const getAdminCompanyRoomInfo = async (roomId: number): Promise<AdminCompanyRoomInfo | undefined> => {
    if (!roomId) return;

    const url = `${backendBasePath}/admin/chat_company_inquiries/${roomId}`;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  type AdminFinancialAgencyRoomInfo = {
    id: number;
    financial_agency_division_name: string;
    financial_agency_division_tel: string;
  };

  const getAdminFinancialAgencyRoomInfo = async (roomId: number): Promise<AdminFinancialAgencyRoomInfo | undefined> => {
    if (!roomId) return;

    const url = `${backendBasePath}/admin/chat_financial_agency_division_inquiries/${roomId}`;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  type AdminFinancialAgencyCompanyRoomInfo = {
    id: number;
    company_id: number;
    company_name: string;
    financial_agency_division_name: string;
    company_tel: string;
    financial_agency_division_tel: string;
    company_image: string;
  };
  const getAdminFinancialAgencyCompanyRoomInfo = async (
    roomId: number,
  ): Promise<AdminFinancialAgencyCompanyRoomInfo | undefined> => {
    if (!roomId) return;

    const url = `${backendBasePath}/admin/chat_consulting_rooms/${roomId}`;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  type FinancialAgencyCompanyRoomInfo = {
    id: number;
    financial_agency_division_name: string;
    company_id: number;
    company_name: string;
    company_tel: string;
    company_image: string | null;
  };
  const getFinancialAgencyCompanyRoomInfo = async (
    roomId: number,
  ): Promise<FinancialAgencyCompanyRoomInfo | undefined> => {
    if (!roomId) return;

    const url = `${backendBasePath}/financial_agency/chat_consulting_rooms/${roomId}`;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  type FinancialAgencySellerRoomInfo = {
    id: number;
    seller_user_name: string;
    financial_agency_division_name: string;
  };
  const getFinancialAgencySellerRoomInfo = async (
    roomId: number,
  ): Promise<FinancialAgencySellerRoomInfo | undefined> => {
    if (!roomId) return;

    const url = `${backendBasePath}/financial_agency/chat_seller_financial_agency_division_rooms/${roomId}`;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  type AdminSellerFinancialAgencyRoomInfo = {
    id: number;
    seller_user_name: string;
    financial_agency_division_name: string;
    financial_agency_division_tel: string;
  };
  const getAdminSellerFinancialAgencyRoomInfo = async (
    roomId: number,
  ): Promise<AdminSellerFinancialAgencyRoomInfo | undefined> => {
    if (!roomId) return;

    const url = `${backendBasePath}/admin/chat_seller_financial_agency_division_rooms/${roomId}`;
    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const updateAdminSellerChatActionRequiredFlag = async (chat_user_inquiry_id: number, submitData: any) => {
    const url = `${backendBasePath}/admin/chat_user_inquiries/${chat_user_inquiry_id}`;

    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: submitData,
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const updateAdminCompanyChatActionRequiredFlag = async (
    chat_company_inquiry_id: number,
    action_required: boolean,
  ) => {
    const url = `${backendBasePath}/admin/chat_company_inquiries/${chat_company_inquiry_id}`;

    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ action_required }),
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const updateAdminCompanyChatReplyRequiredFlag = async (chat_company_inquiry_id: number, reply_required: boolean) => {
    const url = `${backendBasePath}/admin/chat_company_inquiries/${chat_company_inquiry_id}`;

    const result = await fetch(url, {
      method: 'PATCH',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ reply_required }),
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const requestSearchRecommendCompany = async () => {
    const url = `${backendBasePath}/seller/inquiry_chat_items/request_search_company`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getChatFile = async (userType: UserType, chatItemId: number, fileName: string) => {
    if (!chatItemId) return;
    const url = `${backendBasePath}/${userType}/chat_items/${chatItemId}/file_download`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => response.blob())
      .then((blob) => {
        const url = URL.createObjectURL(blob);
        let link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        link.click();
        URL.revokeObjectURL(url);
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const sendCheckSaleProgressMessage = async (roomId: number, requestParams: { resend: boolean }) => {
    const url = `${backendBasePath}/admin/chat_rooms/${roomId}/send_check_sale_progress_message`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(requestParams),
    })
      .then((response) => {
        if (response.status === 200) {
          return response.json();
        } else {
          return response.json();
        }
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const getFinancialAgencyDivisionOptions = async (query?: string) => {
    const url = `${backendBasePath}/admin/financial_agency_divisions/options?q=${query || ''}`;

    const result = await fetch(url, {
      method: 'GET',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
      },
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  const createAdminFinancialAgencyCompanyRoom = async (params: {
    company_id: string;
    financial_agency_division_id: string;
  }) => {
    const url = `${backendBasePath}/admin/chat_consulting_rooms`;

    const result = await fetch(url, {
      method: 'POST',
      mode: 'cors',
      headers: {
        uid: cookies['uid'],
        client: cookies['client'],
        'access-token': cookies['access-token'],
        'Content-Type': 'application/json',
      },
      body: JSON.stringify(params),
    })
      .then((response) => {
        return response.json();
      })
      .catch((error) => {
        Sentry.captureException(error);
        console.error('通信に失敗しました', error);
      });
    return result;
  };

  return {
    getCompanySellerChatRoomList,
    getCompanySellerContactChatRoomList,
    getFinancialAgencySellerChatRoomList,
    getCompanyAdminRoomList,
    getCompanyFinancialAgencyRoomList,
    getAdminSellerRoomList,
    getAdminCompanyRoomList,
    getAdminSellerCompanyRoomList,
    getAdminSellerCompanyContactRoomList,
    getAdminFinancialAgencyRoomList,
    getAdminFinancialAgencyCompanyRoomList,
    getFinancialAgencyAdminRoomList,
    getFinancialAgencyCompanyRoomList,
    getAdminSellerFinancialAgencyRoomList,
    getCompanySellerTalk,
    getCompanyAdminTalk,
    getCompanyFinancialAgencyTalk,
    getAdminSellerTalk,
    getAdminCompanyTalk,
    getAdminSellerCompanyTalk,
    getAdminSellerCompanyContactTalk,
    getAdminFinancialAgencyTalk,
    getAdminFinancialAgencyCompanyTalk,
    getFinancialAgencyAdminTalk,
    getFinancialAgencySellerTalk,
    getFinancialAgencyCompanyTalk,
    createCompanySellerTalk,
    createCompanySellerContactTalk,
    getCompanySellerContactTalk,
    createCompanyAdminTalk,
    createCompanyFinancialAgencyTalk,
    createAdminSellerTalk,
    createAdminCompanyTalk,
    createAdminFinancialAgencyTalk,
    createFinancialAgencyAdminTalk,
    createFinancialAgencyCompanyTalk,
    createFinancialAgencySellerTalk,
    deleteCompanySellerTalk,
    deleteCompanySellerContactTalk,
    deleteCompanyAdminTalk,
    deleteCompanyFinancialAgencyTalk,
    deleteAdminSellerTalk,
    deleteAdminCompanyTalk,
    deleteAdminFinancialAgencyTalk,
    deleteFinancialAgencyAdminTalk,
    deleteFinancialAgencyCompanyTalk,
    deleteFinancialAgencySellerTalk,
    getCompanySellerRoomInfo,
    getCompanySellerContactRoomInfo,
    getCompanyFinancialAgencyRoomInfo,
    getAdminSellerCompanyRoomInfo,
    getAdminSellerCompanyContactRoomInfo,
    getAdminSellerRoomInfo,
    getAdminCompanyRoomInfo,
    getAdminFinancialAgencyRoomInfo,
    getAdminSellerFinancialAgencyRoomInfo,
    getAdminFinancialAgencyCompanyRoomInfo,
    getFinancialAgencyCompanyRoomInfo,
    getFinancialAgencySellerRoomInfo,
    getAdminSellerFinancialAgencyTalk,
    updateAdminSellerChatActionRequiredFlag,
    updateAdminCompanyChatActionRequiredFlag,
    updateAdminCompanyChatReplyRequiredFlag,
    requestSearchRecommendCompany,
    getChatFile,
    sendCheckSaleProgressMessage,
    getFinancialAgencyDivisionOptions,
    createAdminFinancialAgencyCompanyRoom,
  };
};
